/**
 * One room's three questions — price, extra services, start date — asked the same way for every
 * person entering care (owner, 7 Oct 2026: "shouldn't the questions be exactly the same?"). The
 * Analyser renders this once for a single entrant and once per person when both of a couple are
 * moving in, so the two rooms can never drift apart in wording or behaviour.
 */
import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { formatCurrency, parseCurrency } from '../../utils/index.ts';
import { AGED_CARE_SCHEDULE } from '../../constants/index.ts';
import { InfoIcon } from '../../components/ui/index.tsx';
import { formatExtraServiceFee } from './extraServiceFee.ts';

const WhatsThis = ({ text }: { text: string }) => <InfoIcon text={text} />;

export interface RoomInputsProps {
    /** Distinguishes the ids of the two rooms' fields (`''` for the first, `'-second'` for the second). */
    idSuffix?: string;
    roomPrice: number;
    onRoomPrice: (value: number) => void;
    onFacilitySearch: () => void;
    selectedFacility: { name: string; suburb: string; radPrice: number } | null;
    /** Annual extra-service fee; the field itself takes a daily or annual figure. */
    extraServiceAnnual: number;
    onExtraServiceAnnual: (annual: number) => void;
    careStartDate: string;
    /** The date input's own props (a clamped field, or a plain value/onChange pair). */
    dateInputProps: React.InputHTMLAttributes<HTMLInputElement>;
    minDate: string;
    /** The "what's this?" for the start date — differs where a second person may enter later. */
    dateHelp: string;
    /** The date's question; "When is care expected to start?" unless the room is a move. */
    dateLabel?: string;
    /** Shown under the date, blank or chosen. */
    dateHint: { blank: string; chosen: string };
}

const RoomInputs = ({ idSuffix = '', roomPrice, onRoomPrice, onFacilitySearch, selectedFacility, extraServiceAnnual, onExtraServiceAnnual, careStartDate, dateInputProps, minDate, dateHelp, dateHint, dateLabel = 'When is care expected to start?' }: RoomInputsProps) => {
    // The unit the extra-service figure is typed in, and the in-progress keystrokes so a decimal
    // point survives typing. Homes quote per day, so that is the default.
    const [unit, setUnit] = useState<'daily' | 'annual'>('daily');
    const [draft, setDraft] = useState<string | null>(null);
    // Two columns from a laptop up (the mock-up's step 1, 17 Oct 2026): price beside extras, start date beside what follows it.
    return (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    Room price{' '}
                    <WhatsThis text="The price of the room as advertised by the aged care home — the full lump sum it would cost to buy the room outright. You do not have to pay it as a lump sum: step 3 is where you choose how much of it to pay up front and how much to pay daily instead." />
                </label>
                <div className="flex gap-2">
                    {/* Empty, not "$0", so the example shows: a browser only shows a placeholder when the
                        field is empty, and the government's estimator primes readers to type a DAILY figure. */}
                    <input
                        type="text"
                        id={`room-price${idSuffix}`}
                        value={roomPrice > 0 ? formatCurrency(roomPrice) : ''}
                        onChange={(e) => onRoomPrice(parseFloat(String(parseCurrency(e.target.value))) || 0)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g. $500,000 — the whole room price"
                    />
                    <button
                        type="button"
                        onClick={onFacilitySearch}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm"
                    >
                        <Search size={14} />
                        Find a home
                    </button>
                </div>
                {selectedFacility && (
                    <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded-lg">
                        <strong>Selected:</strong> {selectedFacility.name}, {selectedFacility.suburb} - ${selectedFacility.radPrice.toLocaleString()}
                    </div>
                )}
                {/* The same room in the other unit, derived from the schedule's MPIR: homes must publish
                    both, and My Aged Care asks for the daily figure. */}
                {roomPrice > 0 && (
                    <p className="text-xs text-slate-500">
                        That is <strong className="text-slate-700">{formatCurrency((roomPrice * AGED_CARE_SCHEDULE.MPIR) / 365)} a day</strong>{' '}
                        if the whole room were paid daily instead — the same room, quoted the other way.
                        Homes must publish both, and My Aged Care asks for the daily figure.
                    </p>
                )}
            </div>
            <div className="space-y-2">
                <label htmlFor={`extraServiceFee${idSuffix}`} className="block text-sm font-medium text-gray-700">
                    Optional extra services{' '}
                    <WhatsThis text="Some homes charge an extra fee for additional services — better meal choices, hairdressing, newspapers, outings. Homes normally quote this per day, so that is what this field expects; switch to per year if your quote is annual. Leave it at zero if there is no such fee." />
                </label>
                <div className="flex gap-2">
                    <input
                        type="text"
                        id={`extraServiceFee${idSuffix}`}
                        name={`extraServiceFee${idSuffix}`}
                        value={draft ?? formatExtraServiceFee(extraServiceAnnual, unit)}
                        onChange={(e) => {
                            setDraft(e.target.value);
                            const typed = parseFloat(String(parseCurrency(e.target.value)));
                            onExtraServiceAnnual(isNaN(typed) ? 0 : (unit === 'daily' ? typed * 365 : typed));
                        }}
                        onBlur={() => setDraft(null)}
                        placeholder={unit === 'daily' ? 'e.g. $25.00' : 'e.g. $9,125'}
                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                        {(['daily', 'annual'] as const).map((u) => (
                            <button
                                key={u}
                                type="button"
                                aria-pressed={unit === u}
                                onClick={() => { setDraft(null); setUnit(u); }}
                                className={`px-2.5 py-2 whitespace-nowrap transition-colors ${unit === u ? 'bg-blue-600 text-white font-semibold' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                            >
                                {u === 'daily' ? 'per day' : 'per year'}
                            </button>
                        ))}
                    </div>
                </div>
                {extraServiceAnnual > 0 && (
                    <p className="text-xs text-gray-500">
                        {unit === 'daily' ? `${formatCurrency(extraServiceAnnual)} a year` : `${formatCurrency(extraServiceAnnual / 365)} a day`}
                    </p>
                )}
            </div>
            <div className="space-y-2">
                <label htmlFor={`care-start-date${idSuffix}`} className="block text-sm font-medium text-gray-700 mb-1">
                    {dateLabel}{' '}
                    <WhatsThis text={dateHelp} />
                </label>
                <input
                    type="date"
                    id={`care-start-date${idSuffix}`}
                    name={`careStartDate${idSuffix}`}
                    min={minDate}
                    {...dateInputProps}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-slate-500">{careStartDate ? dateHint.chosen : dateHint.blank}</p>
            </div>
        </div>
    );
};

export default RoomInputs;
